import { randomUUID } from './uuid.js';

/**
 * Lock entre abas, com fallback para contexto não seguro.
 *
 * A **Web Locks API** (`navigator.locks`) é a ferramenta certa para impedir que
 * /controle e /admin renovem a sessão ao mesmo tempo — mas ela só existe em
 * HTTPS ou `localhost`. No salão, o operador abre pelo IP da rede local, em
 * http: `navigator.locks` é `undefined` e a coordenação sumiria justamente no
 * ambiente real.
 *
 * O fallback é uma **eleição por `BroadcastChannel`** (que não exige contexto
 * seguro): quem quer o lock anuncia um id aleatório, espera uma janela curta
 * colhendo os anúncios das outras abas, e só executa quem tiver o menor id. Os
 * perdedores aguardam o aviso de liberação — ou o timeout, para uma aba fechada
 * no meio do caminho não travar as demais para sempre.
 *
 * Não é um lock distribuído perfeito, e não precisa ser: a janela de tolerância
 * de 30s do servidor (decisão 1 do CLAUDE.md) é a rede de segurança para o
 * empate raro. O que este código evita é o caso comum — duas abas renovando
 * juntas toda vez que o access token expira.
 */

const ELECTION_WINDOW_MS = 60;
const LOCK_TIMEOUT_MS = 8000;

type Message =
  { kind: 'claim'; lock: string; id: string } | { kind: 'release'; lock: string; id: string };

export function hasWebLocks(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
}

export async function withLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  if (hasWebLocks()) return navigator.locks.request(name, fn);

  if (typeof BroadcastChannel === 'undefined') {
    // Nem Web Locks nem BroadcastChannel: aba sozinha, ou navegador muito
    // antigo. Executa direto — o servidor tolera a corrida.
    return fn();
  }

  const channel = new BroadcastChannel(`lock:${name}`);
  const myId = randomUUID();
  const rivals: string[] = [];
  let releaseResolve: (() => void) | null = null;

  const onMessage = (event: MessageEvent) => {
    const message = event.data as Message | null;
    if (!message || message.lock !== name) return;

    if (message.kind === 'claim' && message.id !== myId) rivals.push(message.id);
    if (message.kind === 'release') releaseResolve?.();
  };

  channel.addEventListener('message', onMessage);

  try {
    channel.postMessage({ kind: 'claim', lock: name, id: myId } satisfies Message);
    await delay(ELECTION_WINDOW_MS);

    const lost = rivals.some((rival) => rival < myId);

    if (lost) {
      // Perdeu a eleição: espera a liberação, com teto. Se a vencedora morreu
      // (aba fechada), o timeout destrava em vez de prender esta aba.
      await Promise.race([
        new Promise<void>((resolve) => {
          releaseResolve = resolve;
        }),
        delay(LOCK_TIMEOUT_MS),
      ]);
    }

    return await fn();
  } finally {
    channel.postMessage({ kind: 'release', lock: name, id: myId } satisfies Message);
    channel.removeEventListener('message', onMessage);
    channel.close();
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
